import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { ASSESSMENT_TYPES } from '../../config/assessmentConfigs';
import { resolveViewMode } from '../../utils/viewMode';
import './index.css';

/**
 * Landing route at `/`.
 *
 * Cold-load auth check: when the user lands here for the first time
 * (no SPA history), `App.js` has just dispatched `loadUser()` but the
 * thunk hasn't resolved yet, so Redux still reports `isAuthenticated`
 * as `null` and `loading: true`. We MUST wait for that to settle —
 * if we redirect to `/login` immediately, an authenticated returning
 * user briefly sees the login form before `loadUser` flips them
 * back to the dashboard. (PrivateRoute already gates on `!loading`
 * when entering protected routes; this component is the missing
 * mirror of that check for the entry route.)
 */
export const LandingPage = () => {
  const { loading, isAuthenticated, user } = useSelector(
    (state) => state.auth
  );

  // 1. Auth check still in flight → spinner. Avoids the login flash.
  if (loading) {
    return (
      <div className="landing-loading">
        <Loading />
      </div>
    );
  }

  // 2. Loaded, no token / invalid token → punt to login.
  if (!isAuthenticated) {
    return <Redirect to='/login' />;
  }

  // 3. Loaded + authenticated → send to the user's natural surface.
  //    Mirrors the post-login routing logic in pages/dashboard/login.js
  //    so direct visits to `/` land in the same place a fresh login
  //    would.
  // Coaches (every trainer-plus role) land in the studio, like a fresh login;
  // /dashboard/home is the CLIENT home and spins forever for them (they have
  // no client profile). "View as client" still goes there.
  const role = user?.role;
  const isTrainerPlus =
    role === 'trainer' || role === 'organization_admin' || role === 'platform_admin';
  const hasCompletedAssessment = user?.isAssessmentCompleted;
  const assessmentType = user?.assessmentType || ASSESSMENT_TYPES.FULL;
  const isAssessmentNone = assessmentType === ASSESSMENT_TYPES.NONE;

  let storedViewMode = 'trainer';
  try {
    storedViewMode = localStorage.getItem('viewMode') || 'trainer';
  } catch (_) {
    // storage blocked — default to the studio
  }
  const target = isTrainerPlus
    ? resolveViewMode(user, storedViewMode) === 'client'
      ? '/dashboard/home'
      : '/dashboard/admin'
    : hasCompletedAssessment
    ? '/dashboard/home'
    : isAssessmentNone
    ? '/quick-setup'
    : '/assessment';

  return <Redirect to={target} />;
};

export default LandingPage;
